import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Typography,
  Box,
  FormControl,
  InputLabel,
  TextField,
  Pagination,
  SelectChangeEvent,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getRMUser } from "../../redux/user/userSlice";
import AppLoader from "../AppLoader/AppLoader";
import {
  EmptyTableRow,
  bodyCellSx,
  formatListingDate,
  headCellSx,
  BORDER,
  ICON_BLUE,
  cardSx,
} from "../AssetListing/assetListingShared";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  OFF_WHITE,
  PROFILE_FONT,
  filterFieldSx,
  muiFieldSx,
} from "../../constants/layout";

const fieldSx = muiFieldSx;

interface User {
  id: number;
  firstName: string;
  email: string;
  mobile: string;
  createdAt: string;
}

interface AssignRMTableProps {
  requests: any;
  totalPages: number;
  rowsPerPage: number;
  page: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loading: boolean;
  error: string | null;
  sortBy: number;
  onChangeStatus: (userType: number, requestId: number, status: number) => void;
  onChangeSortBy: (sortBy: number) => void;
  onOpenDetails: (id: number) => void;
  filterOption: any;
  setFilterOption: (option: any) => void;
}

const AssignRMTable: React.FC<AssignRMTableProps> = ({
  requests,
  totalPages,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
  loading,
  error,
  onChangeStatus,
  sortBy,
  onChangeSortBy,
  filterOption,
  setFilterOption,
}) => {
  const dispatch = useDispatch<any>();
  const [users, setUsers] = useState<User[]>([]);
  const [partners, setPartners] = useState<User[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const sortedRequests = useMemo(() => {
    if (!Array.isArray(requests)) return [];

    return [...requests]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((request) => {
        const query = searchQuery.toLowerCase();
        const fullName = `${request.firstName || ""} ${request.lastName || ""}`.toLowerCase();
        return fullName.includes(query) || (request.email || "").toLowerCase().includes(query);
      });
  }, [requests, searchQuery]);

  const columnCount = filterOption.selectedRM ? 4 : 5;
  const totalRecords = totalPages * rowsPerPage;
  const from = sortedRequests.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const to = sortedRequests.length === 0 ? 0 : (page - 1) * rowsPerPage + sortedRequests.length;

  const handleChangeStatus = async (
    userType: number,
    requestId: number,
    status: number
  ) => {
    setIsAssigning(true);
    setTimeout(async () => {
      await onChangeStatus(userType, requestId, status);
      setIsAssigning(false);
    }, 4000);
  };

  const fetchManagers = async () => {
    try {
      const [rmResponse, partnerResponse] = await Promise.all([
        dispatch(getRMUser({ userType: 2, pageIndex: 1, pageSize: 1000 })),
        dispatch(getRMUser({ userType: 6, pageIndex: 1, pageSize: 1000 })),
      ]);
      setUsers(rmResponse.payload.data || []);
      setPartners(partnerResponse.payload.data || []);
    } catch (fetchError) {
      console.error("Error fetching manager data:", fetchError);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, [dispatch]);

  const renderManagerSelect = (
    value: number | string,
    onChange: (selectedValue: number) => void,
    disabled?: boolean
  ) => (
    <FormControl size="small" fullWidth sx={{ ...fieldSx, minWidth: 180 }} disabled={disabled}>
      <Select
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value))}
        displayEmpty
        sx={{
          "& .MuiSelect-select": {
            fontFamily: PROFILE_FONT,
            fontSize: 14,
            color: value ? DARK_NAVY : DARK_NAVY_LIGHT,
          },
        }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography component="span" sx={{ color: DARK_NAVY_LIGHT, fontSize: 14 }}>
                Select manager / partner
              </Typography>
            );
          }
          const rm = users.find((user) => user.id === selected);
          const partner = partners.find((user) => user.id === selected);
          return rm?.firstName || partner?.firstName || String(selected);
        }}
      >
        <MenuItem value="" disabled>
          Select Manager
        </MenuItem>
        {users.map((user) => (
          <MenuItem key={`rm-${user.id}`} value={user.id}>
            {user.firstName} (RM)
          </MenuItem>
        ))}
        {partners.length > 0 ? (
          <MenuItem value="" disabled>
            Select Partner
          </MenuItem>
        ) : null}
        {partners.map((user) => (
          <MenuItem key={`partner-${user.id}`} value={user.id}>
            {user.firstName} (Partner)
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <AppLoader loading={isAssigning || loading} />

      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: { xs: 24, md: 28 },
              fontWeight: 700,
              color: DARK_NAVY,
              mb: 0.5,
            }}
          >
            Assign RM / Partner
          </Typography>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
            Assign relation managers or partners to registered users.
          </Typography>
        </Box>

        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 1fr" },
              gap: 2,
              alignItems: "end",
            }}
          >
            <TextField
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              variant="outlined"
              fullWidth
              sx={filterFieldSx}
            />

            <FormControl size="small" sx={filterFieldSx} fullWidth>
              <InputLabel id="assign-sort-label">Sort By</InputLabel>
              <Select
                labelId="assign-sort-label"
                value={sortBy}
                label="Sort By"
                onChange={(e) => onChangeSortBy(Number(e.target.value))}
              >
                <MenuItem value={1}>Name (A-Z)</MenuItem>
                <MenuItem value={2}>Name (Z-A)</MenuItem>
                <MenuItem value={3}>Date (Oldest First)</MenuItem>
                <MenuItem value={4}>Date (Newest First)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={filterFieldSx} fullWidth disabled={isAssigning}>
              <InputLabel id="assign-filter-label">Filter by Manager</InputLabel>
              <Select
                labelId="assign-filter-label"
                label="Filter by Manager"
                value={filterOption.selectedRM || "ALL"}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (selectedValue === "ALL") {
                    setFilterOption({
                      ...filterOption,
                      selectedRM: "",
                      isRM: false,
                    });
                    return;
                  }
                  const isUser = users.some((user) => user.id === selectedValue);
                  setFilterOption({
                    ...filterOption,
                    selectedRM: selectedValue,
                    isRM: isUser ? 2 : 6,
                  });
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                {users.map((user) => (
                  <MenuItem key={`filter-rm-${user.id}`} value={user.id}>
                    {user.firstName} (RM)
                  </MenuItem>
                ))}
                {partners.map((user) => (
                  <MenuItem key={`filter-partner-${user.id}`} value={user.id}>
                    {user.firstName} (Partner)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>User Name</TableCell>
                  <TableCell sx={headCellSx}>User Email</TableCell>
                  {!filterOption.selectedRM ? (
                    <TableCell sx={headCellSx}>Relation Manager / Partner</TableCell>
                  ) : null}
                  <TableCell sx={headCellSx}>Assign Manager / Partner</TableCell>
                  <TableCell sx={headCellSx}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableRow>
                    <EmptyTableRow colSpan={columnCount} message={error} />
                  </TableRow>
                ) : sortedRequests.length === 0 ? (
                  <TableRow>
                    <EmptyTableRow colSpan={columnCount} message="No users found" />
                  </TableRow>
                ) : (
                  sortedRequests.map((request) => (
                    <TableRow key={request.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                      <TableCell sx={bodyCellSx}>
                        {`${request.firstName || ""} ${request.lastName || ""}`.trim() || "—"}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }}>{request.email}</TableCell>
                      {!filterOption.selectedRM ? (
                        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
                          {request?.relationManager?.rmName || "N/A"}
                        </TableCell>
                      ) : null}
                      <TableCell sx={bodyCellSx}>
                        {renderManagerSelect(
                          request?.requestStatus || "",
                          (selectedValue) => {
                            const isUser = users.some((user) => user.id === selectedValue);
                            handleChangeStatus(isUser ? 2 : 6, request.id, selectedValue);
                          },
                          isAssigning
                        )}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY }}>
                        {formatListingDate(request.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2, md: 2.5 },
              py: 2,
              borderTop: `1px solid ${BORDER}`,
            }}
          >
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT }}>
              Showing {from} to {to} of {totalRecords} users
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ ...fieldSx, minWidth: 130 }}>
                <Select
                  value={rowsPerPage}
                  onChange={(e: SelectChangeEvent<number>) =>
                    onRowsPerPageChange({
                      target: { value: String(e.target.value) },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                >
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={25}>25 per page</MenuItem>
                  <MenuItem value={50}>50 per page</MenuItem>
                  <MenuItem value={100}>100 per page</MenuItem>
                </Select>
              </FormControl>

              <Pagination
                count={totalPages || 1}
                page={page}
                onChange={(_, newPage) => onPageChange(null, newPage - 1)}
                shape="rounded"
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontFamily: PROFILE_FONT,
                    fontSize: 13,
                    color: DARK_NAVY_LIGHT,
                    borderRadius: "8px",
                  },
                  "& .Mui-selected": {
                    bgcolor: `${ICON_BLUE} !important`,
                    color: "#ffffff !important",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AssignRMTable;
